import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Circle,
    Database,
    Globe,
    Layers,
    Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { rayicIstihbarat } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { RAYIC_WORKFLOW } from "./rayicUtils";

const STEP_ORDER = RAYIC_WORKFLOW.map((s) => s.id);

export function RayicWorkflowStrip({ activeStep }) {
    const idx = STEP_ORDER.indexOf(activeStep);
    return (
        <div className="flex items-center gap-1 px-3 py-1.5 border-b border-white/5 bg-[#0a0f18]/50 overflow-x-auto shrink-0">
            {RAYIC_WORKFLOW.map((s, i) => {
                const done = i < idx;
                const active = i === idx;
                return (
                    <React.Fragment key={s.id}>
                        {i > 0 ? <div className="h-px w-3 sm:w-6 bg-white/10 shrink-0" /> : null}
                        <div
                            className={`flex items-center gap-1 shrink-0 rounded-md px-2 py-0.5 ${
                                active
                                    ? "bg-[#D4AF37]/15 border border-[#D4AF37]/30"
                                    : done
                                      ? "opacity-85"
                                      : "opacity-45"
                            }`}
                        >
                            {done ? (
                                <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                            ) : (
                                <Circle className={`h-3 w-3 ${active ? "text-[#D4AF37]" : "text-slate-600"}`} />
                            )}
                            <div>
                                <div className={`text-[10px] font-medium ${active ? "text-white" : "text-slate-400"}`}>
                                    {s.label}
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
}

export function RayicKpiChip({ label, value, hint, accent = "gold" }) {
    const ring =
        accent === "red"
            ? "border-rose-500/35 bg-rose-500/10 text-rose-200"
            : accent === "emerald"
              ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-200"
              : "border-[#D4AF37]/35 bg-[#D4AF37]/10 text-[#F3E5AB]";
    return (
        <div className={`rounded-md border px-2 py-1.5 min-w-0 ${ring}`}>
            <div className="text-[9px] uppercase tracking-wide text-slate-500 truncate">{label}</div>
            <div className="text-sm font-semibold tabular-nums leading-tight mt-0.5">{value ?? "—"}</div>
            {hint ? <div className="text-[9px] text-slate-500 mt-0.5 truncate">{hint}</div> : null}
        </div>
    );
}

export function KaynakBadge({ kaynak }) {
    if (kaynak === "ic_kayit_medyan" || kaynak === "ic_kayit")
        return (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-sky-500/40 text-sky-300 bg-sky-500/10">
                <Database className="h-3 w-3" /> MBS iç
            </span>
        );
    if (kaynak === "cok_kaynak")
        return (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-cyan-500/40 text-cyan-300 bg-cyan-500/10">
                <Layers className="h-3 w-3" /> Çok kaynak
            </span>
        );
    if (kaynak === "dis_tahmin" || kaynak === "piyasa_ilan")
        return (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-violet-500/40 text-violet-300 bg-violet-500/10">
                <Globe className="h-3 w-3" /> Canlı piyasa
            </span>
        );
    return null;
}

export function ParcaDurumBadge({ durum, label }) {
    const cls =
        durum === "yuksek"
            ? "border-rose-500/40 text-rose-300 bg-rose-500/10"
            : durum === "uyumlu" || durum === "piyasa_bulundu"
              ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
              : durum === "dusuk"
                ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                : "border-slate-500/40 text-slate-400 bg-slate-500/10";
    return (
        <span className={`inline-flex text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>
            {label || durum || "—"}
        </span>
    );
}

export function DosyaMuvekkilSelect({ value, onChange, onDosyaMeta, className = "" }) {
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [data, setData] = useState({ muvekkiller: [], dosyalar: [] });
    const [muvekkilId, setMuvekkilId] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await rayicIstihbarat.dosyaSecim({
                q: q.trim() || undefined,
                muvekkil_id: muvekkilId || undefined,
                aktif: 1,
            });
            setData({ muvekkiller: res.muvekkiller || [], dosyalar: res.dosyalar || [] });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Dosya listesi yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, [q, muvekkilId]);

    useEffect(() => {
        const t = setTimeout(() => load(), q ? 280 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    const grouped = useMemo(() => {
        const map = new Map();
        for (const d of data.dosyalar || []) {
            if (!map.has(d.muvekkil_id))
                map.set(d.muvekkil_id, {
                    muvekkil_id: d.muvekkil_id,
                    muvekkil_ad_soyad: d.muvekkil_ad_soyad,
                    items: [],
                });
            map.get(d.muvekkil_id).items.push(d);
        }
        return Array.from(map.values());
    }, [data.dosyalar]);

    return (
        <div className={`space-y-1.5 ${className}`}>
            <div className="flex gap-1.5">
                <select
                    value={muvekkilId}
                    onChange={(e) => setMuvekkilId(e.target.value)}
                    className="flex-1 rounded-md bg-[#0a1220] border border-white/10 px-2 py-1.5 text-[10px] text-white"
                >
                    <option value="">Tüm müvekkiller</option>
                    {(data.muvekkiller || []).map((m) => (
                        <option key={m.id} value={m.id}>
                            {m.label || m.ad_soyad}
                        </option>
                    ))}
                </select>
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Ara…"
                    className="flex-[1.2] bg-black/20 border-white/10 text-[10px] h-8"
                />
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400 self-center" /> : null}
            </div>
            <select
                value={value}
                onChange={(e) => {
                    const id = e.target.value;
                    onChange(id);
                    const row = (data.dosyalar || []).find((x) => String(x.id) === String(id));
                    if (row && onDosyaMeta) onDosyaMeta(row);
                }}
                className="w-full rounded-md bg-[#0a1220] border border-white/10 px-2 py-1.5 text-xs text-white"
            >
                <option value="">Müvekkil / dosya seçin</option>
                {grouped.map((g) => (
                    <optgroup key={g.muvekkil_id} label={g.muvekkil_ad_soyad || `#${g.muvekkil_id}`}>
                        {g.items.map((d) => (
                            <option key={d.id} value={d.id}>
                                {d.label || d.dosya_no}
                            </option>
                        ))}
                    </optgroup>
                ))}
            </select>
        </div>
    );
}

export function RayicAccordion({ title, badge, children, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="rounded-lg border border-white/10 bg-[#121A2F]/50 overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.03]"
            >
                <span className="text-xs font-medium text-white flex-1">{title}</span>
                {badge != null ? <span className="text-[10px] font-mono text-slate-500">{badge}</span> : null}
                {open ? <ChevronUp className="h-3.5 w-3.5 text-slate-500" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-500" />}
            </button>
            {open ? <div className="px-3 pb-3 border-t border-white/5 pt-2">{children}</div> : null}
        </div>
    );
}

/** Eski KpiCard — geriye uyumluluk */
export function KpiCard(props) {
    return <RayicKpiChip {...props} />;
}
